import { useEffect, useRef, useState } from 'react';

import { parseEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';

import {
  QualityGateOption,
  SonarInstanceName,
  qualityGatesApiRef,
} from './QualityGatesApi';

const VCS_PROVIDER_GITHUB = 'github';
const VCS_PROVIDER_BITBUCKET = 'bitbucket';
const DEFAULT_BITBUCKET_SONAR_ORG = 'carrier-digital';

/** Minimal fallback when SonarCloud is unavailable (C2). */
export const FALLBACK_QUALITY_GATES: QualityGateOption[] = [
  { id: -1, name: 'The Foundry Way' },
  { id: -2, name: 'The Abound Way' },
];

export function toQualityGatesError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error('Unknown error');
  }
}

export type ResolveSonarOrganizationOptions = {
  formData?: Record<string, unknown>;
  uiOptions?: Record<string, unknown>;
  /**
   * VCS provider from the form (`bitbucket` | `github`).
   * Optional: undefined is treated as the default (bitbucket) path.
   */
  vcsProvider?: string;
};

function readTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveGithubSonarOrganization(
  formData: Record<string, unknown>,
  uiOptions: Record<string, unknown>,
): string | undefined {
  const githubOrgField = (uiOptions.githubOrgField as string) || 'github_org';
  const githubOrgRef = readTrimmedString(formData[githubOrgField]);
  if (!githubOrgRef) {
    return undefined;
  }

  try {
    const { name } = parseEntityRef(githubOrgRef, {
      defaultKind: 'Group',
    });
    const normalized = name.replace(/^ghe-/, '');
    return normalized ? `gh-${normalized}` : undefined;
  } catch {
    const normalized = githubOrgRef.replace(/^ghe-/, '');
    return normalized ? `gh-${normalized}` : undefined;
  }
}

function resolveBitbucketSonarOrganization(
  uiOptions: Record<string, unknown>,
): string {
  return (
    readTrimmedString(uiOptions.bitbucketOrganization) ||
    DEFAULT_BITBUCKET_SONAR_ORG
  );
}

/**
 * Resolve the SonarCloud organization key used by existing scaffolder templates.
 *
 * Priority:
 * 1. ui:options.organization (static override)
 * 2. form field from ui:options.organizationField (opt-in only when set)
 * 3. GitHub: gh-<github_org name> (strips ghe- prefix)
 * 4. Bitbucket/default: ui:options.bitbucketOrganization or "carrier-digital"
 */
export function resolveSonarOrganization(
  options: ResolveSonarOrganizationOptions,
): string | undefined {
  const { formData = {}, uiOptions = {}, vcsProvider } = options;

  const staticOrganization = readTrimmedString(uiOptions.organization);
  if (staticOrganization) {
    return staticOrganization;
  }

  // H5: only read a form field when templates opt in via organizationField
  const organizationField = uiOptions.organizationField;
  if (typeof organizationField === 'string' && organizationField.trim()) {
    const fromForm = readTrimmedString(formData[organizationField]);
    if (fromForm) {
      return fromForm;
    }
  }

  if (vcsProvider === VCS_PROVIDER_GITHUB) {
    return resolveGithubSonarOrganization(formData, uiOptions);
  }

  // bitbucket, undefined, or any other value → default Bitbucket Sonar org
  return resolveBitbucketSonarOrganization(uiOptions);
}

/**
 * Hook to fetch SonarCloud Quality Gates with loading and error states.
 * C1: loading starts true when an organization is set so PickerSelect does not
 * clear a selected value before the first fetch completes.
 * C2: on failure, keep last good options or use a minimal static fallback.
 */
export function useQualityGates(
  organization?: string,
  instance: SonarInstanceName = 'default',
) {
  const qualityGatesApi = useApi(qualityGatesApiRef);
  const [qualityGates, setQualityGates] = useState<QualityGateOption[]>([]);
  const [loading, setLoading] = useState<boolean>(() => !!organization);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [usingFallback, setUsingFallback] = useState(false);

  const requestIdRef = useRef<number>(0);
  const lastGoodGatesRef = useRef<QualityGateOption[]>([]);

  useEffect(() => {
    if (!organization) {
      setQualityGates([]);
      setLoading(false);
      setError(undefined);
      setUsingFallback(false);
      return (): void => {};
    }

    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;

    const run = async () => {
      setLoading(true);
      setError(undefined);
      setUsingFallback(false);
      try {
        const data = await qualityGatesApi.getQualityGates(
          organization,
          instance,
        );

        if (isMounted && currentRequestId === requestIdRef.current) {
          lastGoodGatesRef.current = data.qualityGates;
          setQualityGates(data.qualityGates);
          setUsingFallback(false);
        }
      } catch (e: unknown) {
        if (isMounted && currentRequestId === requestIdRef.current) {
          setError(toQualityGatesError(e));
          // Keep last good list when available; otherwise use static fallback (C1/C2)
          if (lastGoodGatesRef.current.length > 0) {
            setQualityGates(lastGoodGatesRef.current);
            setUsingFallback(false);
          } else {
            setQualityGates(FALLBACK_QUALITY_GATES);
            setUsingFallback(true);
          }
        }
      } finally {
        if (isMounted && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    run();

    return (): void => {
      isMounted = false;
    };
  }, [organization, instance, qualityGatesApi]);

  return { qualityGates, loading, error, usingFallback };
}

export { VCS_PROVIDER_BITBUCKET, VCS_PROVIDER_GITHUB };
